import { useState } from 'react';
import { allows, ApiError, request, type CurrentUser } from '../../../api';
import { useResource } from '../../../shell/hooks';
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Moment,
  Resourceful,
  SplitGrid,
  TextInput,
  type Column,
} from '../../../shell/kit';
import { useToast } from '../../../shell/toast';
import type { IdentityTranslate } from '../i18n';

/**
 * Юрлица и корпоративный автопарк (ТЗ §5.3.7).
 *
 * Схема под автопарк была с первой миграции, а экрана не было: корпоративного
 * клиента заводили частным лицом, и счёт компании собрать было не по чему.
 * Здесь юрлицо заводится по БИН, к нему добавляются машины парка и водители.
 */

interface CompanySummary {
  readonly id: string;
  readonly name: string | null;
  readonly bin: string | null;
  readonly vehicles: number;
}

interface CompanyCard {
  readonly id: string;
  readonly name: string | null;
  readonly bin: string | null;
  readonly phone: string | null;
  readonly vehicles: ReadonlyArray<{
    readonly vehicleId: string;
    readonly plate: string;
    readonly make: string | null;
    readonly model: string | null;
    readonly since: string;
  }>;
  readonly drivers: ReadonlyArray<{
    readonly customerId: string;
    readonly displayName: string | null;
    readonly phone: string | null;
  }>;
  readonly visits: { readonly total: number; readonly last30d: number };
}

const base = '/api/v1/identity/companies';
const cabinetBase = '/api/v1/cabinet/staff/companies';

interface CompanyManager {
  readonly id: string;
  readonly displayName: string | null;
  readonly phone: string | null;
  readonly grantedAt: string;
}

export function CompaniesView({ t, user }: { t: IdentityTranslate; user: CurrentUser }) {
  const toast = useToast();
  const canEdit = allows(user, 'customer.create');
  // Доступ в корпоративный кабинет открывает только сетевой администратор.
  const canManageAccess = allows(user, 'company.manage');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [bin, setBin] = useState('');
  const [plate, setPlate] = useState('');
  const [driverPhone, setDriverPhone] = useState('');
  const [driverName, setDriverName] = useState('');
  const [busy, setBusy] = useState(false);
  const [managerPhone, setManagerPhone] = useState('');
  const [managerName, setManagerName] = useState('');

  const list = useResource(
    () => request<{ companies: readonly CompanySummary[] }>('GET', `${base}?q=${encodeURIComponent(query)}`),
    [query],
  );
  const card = useResource(
    () => request<CompanyCard>('GET', `${base}/${encodeURIComponent(selected ?? '')}`),
    [selected],
    { enabled: selected !== null },
  );

  // Представители юрлица в корпоративном кабинете (ТЗ М4.8).
  const managers = useResource(
    () =>
      request<{ managers: readonly CompanyManager[] }>(
        'GET',
        `${cabinetBase}/${encodeURIComponent(selected ?? '')}/managers`,
      ),
    [selected],
    { enabled: selected !== null },
  );

  const run = async (action: () => Promise<unknown>, done: string): Promise<void> => {
    setBusy(true);
    try {
      await action();
      toast.ok(done);
      list.reload();
      card.reload();
      managers.reload();
    } catch (err) {
      if (err instanceof ApiError) toast.error(err.message, err.code);
      else toast.error(String((err as Error).message));
    } finally {
      setBusy(false);
    }
  };

  const columns: ReadonlyArray<Column<CompanySummary>> = [
    { key: 'name', header: t('identity.company.name'), render: (r) => r.name ?? '—' },
    { key: 'bin', header: t('identity.card.bin'), render: (r) => <span className="font-mono">{r.bin}</span> },
    { key: 'vehicles', header: t('identity.company.fleet'), numeric: true, render: (r) => r.vehicles },
    {
      key: 'open',
      header: '',
      render: (r) => <Button onClick={() => setSelected(r.id)}>{t('identity.company.open')}</Button>,
    },
  ];

  return (
    <SplitGrid>
      <div className="flex flex-col gap-4">
        <Card title={t('identity.company.list')}>
          <div className="mb-3">
            <Field label={t('identity.company.search')}>
              <TextInput value={query} onChange={(e) => setQuery(e.target.value)} />
            </Field>
          </div>
          <Resourceful resource={list}>
            {(data) => (
              <DataTable
                rows={data.companies}
                columns={columns}
                rowKey={(r) => r.id}
                empty={t('identity.company.none')}
              />
            )}
          </Resourceful>
        </Card>

        {canEdit ? (
          <Card title={t('identity.company.create')}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('identity.company.name')}>
                <TextInput value={name} onChange={(e) => setName(e.target.value)} />
              </Field>
              <Field label={t('identity.card.bin')} hint={t('identity.company.binHint')}>
                <TextInput inputMode="numeric" value={bin} onChange={(e) => setBin(e.target.value)} />
              </Field>
            </div>
            <div className="mt-3">
              <Button
                tone="primary"
                disabled={busy || name.trim().length < 2 || bin.replace(/\D/g, '').length !== 12}
                onClick={() =>
                  void run(async () => {
                    const made = await request<CompanyCard>('POST', base, { name, bin });
                    setSelected(made.id);
                    setName('');
                    setBin('');
                  }, t('identity.company.created'))
                }
              >
                {t('identity.company.create')}
              </Button>
            </div>
          </Card>
        ) : null}
      </div>

      <div className="flex flex-col gap-4">
        {selected === null ? (
          <EmptyState title={t('identity.company.pick')} reason={t('identity.company.pickReason')} />
        ) : (
          <Resourceful resource={card}>
            {(company) => (
              <div className="flex flex-col gap-4">
                <Card
                  title={company.name ?? '—'}
                  hint={`${t('identity.card.bin')} ${company.bin ?? '—'} · ${t('identity.company.visits', {
                    total: company.visits.total,
                    recent: company.visits.last30d,
                  })}`}
                >
                  <DataTable
                    rows={company.vehicles}
                    rowKey={(r) => r.vehicleId}
                    empty={t('identity.company.noFleet')}
                    columns={[
                      { key: 'plate', header: t('identity.card.plate'), render: (r) => <span className="font-mono">{r.plate}</span> },
                      { key: 'car', header: t('identity.card.make'), render: (r) => [r.make, r.model].filter(Boolean).join(' ') || '—' },
                      { key: 'since', header: t('identity.card.since'), render: (r) => <Moment iso={r.since} /> },
                      ...(canEdit
                        ? [
                            {
                              key: 'rm',
                              header: '',
                              render: (r: CompanyCard['vehicles'][number]) => (
                                <Button
                                  disabled={busy}
                                  onClick={() =>
                                    void run(
                                      () =>
                                        request(
                                          'DELETE',
                                          `${base}/${encodeURIComponent(company.id)}/vehicles/${encodeURIComponent(r.vehicleId)}`,
                                        ),
                                      t('identity.company.vehicleRemoved'),
                                    )
                                  }
                                >
                                  {t('identity.company.remove')}
                                </Button>
                              ),
                            },
                          ]
                        : []),
                    ]}
                  />
                  {canEdit ? (
                    <div className="mt-3 flex flex-wrap items-end gap-2">
                      <Field label={t('identity.company.addVehicle')}>
                        <TextInput value={plate} onChange={(e) => setPlate(e.target.value)} />
                      </Field>
                      <Button
                        disabled={busy || plate.trim().length < 2}
                        onClick={() =>
                          void run(async () => {
                            await request('POST', `${base}/${encodeURIComponent(company.id)}/vehicles`, { plate });
                            setPlate('');
                          }, t('identity.company.vehicleAdded'))
                        }
                      >
                        {t('identity.company.add')}
                      </Button>
                    </div>
                  ) : null}
                </Card>

                <Card title={t('identity.company.managers')} hint={t('identity.company.managersHint')}>
                  <Resourceful resource={managers}>
                    {(data) => (
                      <DataTable
                        rows={data.managers}
                        rowKey={(r) => r.id}
                        empty={t('identity.company.noManagers')}
                        columns={[
                          { key: 'name', header: t('identity.card.name'), render: (r) => r.displayName ?? t('identity.card.noName') },
                          { key: 'phone', header: t('identity.card.phone'), render: (r) => r.phone ?? '—' },
                          { key: 'since', header: t('identity.card.since'), render: (r) => <Moment iso={r.grantedAt} /> },
                          ...(canManageAccess
                            ? [
                                {
                                  key: 'rm',
                                  header: '',
                                  render: (r: CompanyManager) => (
                                    <Button
                                      disabled={busy}
                                      onClick={() =>
                                        void run(
                                          () =>
                                            request(
                                              'DELETE',
                                              `${cabinetBase}/${encodeURIComponent(company.id)}/managers/${encodeURIComponent(r.id)}`,
                                            ),
                                          t('identity.company.revoked'),
                                        )
                                      }
                                    >
                                      {t('identity.company.revoke')}
                                    </Button>
                                  ),
                                },
                              ]
                            : []),
                        ]}
                      />
                    )}
                  </Resourceful>
                  {canManageAccess ? (
                    <div className="mt-3 flex flex-wrap items-end gap-2">
                      <Field label={t('identity.card.phone')}>
                        <TextInput value={managerPhone} onChange={(e) => setManagerPhone(e.target.value)} />
                      </Field>
                      <Field label={t('identity.card.name')}>
                        <TextInput value={managerName} onChange={(e) => setManagerName(e.target.value)} />
                      </Field>
                      <Button
                        disabled={busy || managerPhone.replace(/\D/g, '').length < 10}
                        onClick={() =>
                          void run(async () => {
                            await request('POST', `${cabinetBase}/${encodeURIComponent(company.id)}/managers`, {
                              phone: managerPhone,
                              ...(managerName.trim() ? { displayName: managerName.trim() } : {}),
                            });
                            setManagerPhone('');
                            setManagerName('');
                          }, t('identity.company.granted'))
                        }
                      >
                        {t('identity.company.grant')}
                      </Button>
                    </div>
                  ) : null}
                </Card>

                <Card title={t('identity.company.drivers')}>
                  <DataTable
                    rows={company.drivers}
                    rowKey={(r) => r.customerId}
                    empty={t('identity.company.noDrivers')}
                    columns={[
                      { key: 'name', header: t('identity.card.name'), render: (r) => r.displayName ?? t('identity.card.noName') },
                      { key: 'phone', header: t('identity.card.phone'), render: (r) => r.phone ?? '—' },
                      ...(canEdit
                        ? [
                            {
                              key: 'rm',
                              header: '',
                              render: (r: CompanyCard['drivers'][number]) => (
                                <Button
                                  disabled={busy}
                                  onClick={() =>
                                    void run(
                                      () =>
                                        request(
                                          'DELETE',
                                          `${base}/${encodeURIComponent(company.id)}/drivers/${encodeURIComponent(r.customerId)}`,
                                        ),
                                      t('identity.company.driverRemoved'),
                                    )
                                  }
                                >
                                  {t('identity.company.remove')}
                                </Button>
                              ),
                            },
                          ]
                        : []),
                    ]}
                  />
                  {canEdit ? (
                    <div className="mt-3 flex flex-wrap items-end gap-2">
                      <Field label={t('identity.card.phone')}>
                        <TextInput value={driverPhone} onChange={(e) => setDriverPhone(e.target.value)} />
                      </Field>
                      <Field label={t('identity.card.name')}>
                        <TextInput value={driverName} onChange={(e) => setDriverName(e.target.value)} />
                      </Field>
                      <Button
                        disabled={busy || driverPhone.replace(/\D/g, '').length < 10}
                        onClick={() =>
                          void run(async () => {
                            await request('POST', `${base}/${encodeURIComponent(company.id)}/drivers`, {
                              phone: driverPhone,
                              ...(driverName.trim() ? { displayName: driverName.trim() } : {}),
                            });
                            setDriverPhone('');
                            setDriverName('');
                          }, t('identity.company.driverAdded'))
                        }
                      >
                        {t('identity.company.add')}
                      </Button>
                    </div>
                  ) : null}
                </Card>
              </div>
            )}
          </Resourceful>
        )}
      </div>
    </SplitGrid>
  );
}
